import { supabase } from "@/integrations/supabase/client";
import { ApiError } from "@/lib/api-error";

const MAX_ERROR_BODY_CHARS = 1000;

async function getAuthToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Usuário não autenticado");
  }
  return session.access_token;
}

interface ApiFetchOptions extends RequestInit {
  /** Se true (default), injeta `Authorization: Bearer <token>` do Supabase. */
  auth?: boolean;
}

/**
 * Wrapper único para chamadas REST à API. Lança `ApiError` (com status) em
 * resposta não-OK, e deixa erros de rede (`TypeError: Failed to fetch`)
 * propagarem como vêm do `fetch`. Ambos são reportados ao Sentry pelo handler
 * global do TanStack Query (ver App.tsx) quando passam por queries/mutations.
 */
export async function apiFetch<T = unknown>(
  url: string,
  { auth = true, headers, ...init }: ApiFetchOptions = {},
): Promise<T> {
  const finalHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...(headers as Record<string, string> | undefined),
  };
  if (auth) {
    finalHeaders.Authorization = `Bearer ${await getAuthToken()}`;
  }

  const response = await fetch(url, { ...init, headers: finalHeaders });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new ApiError({
      status: response.status,
      url,
      method: (init.method || "GET").toUpperCase(),
      body: text.slice(0, MAX_ERROR_BODY_CHARS),
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}
